import React, { useRef, useState, useEffect } from "react";
import { View, StyleSheet, Text, Animated, PanResponder } from "react-native";

import { useTranslation } from "react-i18next";
import Ionicons from "@expo/vector-icons/Ionicons";
import ChatManager from "../Classes/ChatManager";
import { observer, Observer } from "mobx-react";
import { Chat, MessageStatus } from "../../Resources/Constants";
import { formatTimestamp } from "../utils/dateUtils";
import LoadingLogo from "../Components/LoadingLogo";

import GlobalStyles from "../../Resources/Styles";
import Colors from "../../Resources/Color";
import Fonts from "../../Resources/Fonts";
import { TouchableOpacity } from "react-native";
import ImageComponent from "../Components/ImageComponent";
import { audioFormatTime } from "../utils/numberUtils";
import AudioManager from "../Classes/AudioManager";
import { Slider } from "@react-native-assets/slider";
import MediaManager from "../Classes/MediaManager";
import Clipboard from "@react-native-clipboard/clipboard";
import LinkableText from "../Components/LinkableText";
import { getSlotColorWithOpacity } from "../utils/colorUtils";
import { useDevice } from "../_layout";

const ChatMessageRowView = React.memo(
  ({ message, previousSame, nextSame, isFirstMessage, fullScreen = false }) => {
    const { i18n } = useTranslation();
    const { isMobile } = useDevice();

    const chatManager = ChatManager.getInstance();

    const [isShowingTimestamp, setIsShowingTimestamp] = useState(false);

    const [isHovered, setIsHovered] = useState(false);

    const [offsetX] = useState(new Animated.Value(0));
    const hasTriggeredHaptic = useRef(false);

    const panResponder = PanResponder.create({
      onMoveShouldSetPanResponder: (_, gestureState) =>
        Math.abs(gestureState.dx) > 20,

      onPanResponderMove: (_, gestureState) => {
        if (gestureState.dx > 0) {
          offsetX.setValue(gestureState.dx);
        }

        if (gestureState.dx >= 32 && !hasTriggeredHaptic.current) {
          hasTriggeredHaptic.current = true; // prevent duplicate haptic
          chatManager.setProperty("selectedReplyToId", message.id.toString());
        }
      },

      onPanResponderRelease: () => {
        hasTriggeredHaptic.current = false; // reset for next gesture
        Animated.spring(offsetX, {
          toValue: 0,
          useNativeDriver: true,
        }).start();
      },

      onPanResponderTerminate: () => {
        hasTriggeredHaptic.current = false;
        Animated.spring(offsetX, {
          toValue: 0,
          useNativeDriver: true,
        }).start();
      },
    });

    useEffect(() => {
      setIsShowingTimestamp(false);
    }, [message?.id]);

    if (!message) {
      return null;
    }
    return (
      <Animated.View
        style={{
          transform: [{ translateX: offsetX }],
          marginBottom: nextSame ? 4 : 12,
          marginTop: isFirstMessage ? 12 : 0,
          paddingHorizontal: fullScreen ? 16 : 8,
          flexDirection: "row",
          justifyContent: !message?.isFromCoach ? "flex-start" : "flex-end",
        }}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        {...panResponder.panHandlers}
      >
        {message.isFromCoach && isHovered && (
          <View style={{ alignSelf: "center", flexDirection: "row" }}>
            {isShowingTimestamp && message.type != "audio" && (
              <TouchableOpacity
                onPress={() => Clipboard.setString(message.text)}
                style={{
                  alignSelf: "center",
                  marginHorizontal: 0,
                }}
              >
                <Ionicons
                  name="copy-outline"
                  size={20}
                  color={Colors.messageLightColor}
                />
              </TouchableOpacity>
            )}

            <TouchableOpacity
              onPress={() =>
                chatManager.setProperty(
                  "selectedReplyToId",
                  message.id.toString()
                )
              }
              style={{
                alignSelf: "center",
                paddingHorizontal: 4,
                marginHorizontal: 4,
              }}
            >
              <Ionicons
                name="arrow-undo-circle"
                size={24}
                color={Colors.messageLightColor}
              />
            </TouchableOpacity>
          </View>
        )}

        <View
          style={{
            alignItems: !message?.isFromCoach ? "flex-start" : "flex-end",
            maxWidth:
              message.type === "image" || message.type === "video"
                ? Chat.MEDIA_WIDTH
                : "75%",

            transform: [
              {
                translateX:
                  message.status === MessageStatus.ERROR_MEDIA ||
                  message.status === MessageStatus.ERROR_MESSAGE
                    ? 0
                    : 0,
              },
            ],
          }}
        >
          {message.replyTo && <ReplyToView message={message} />}
          <TouchableOpacity
            style={{
              borderRadius: 12,
              borderTopRightRadius:
                previousSame && message.isFromCoach ? 4 : 12,
              borderBottomRightRadius: nextSame && message.isFromCoach ? 4 : 12,
              borderTopLeftRadius:
                previousSame && !message.isFromCoach ? 4 : 12,
              borderBottomLeftRadius: nextSame && !message.isFromCoach ? 4 : 12,
              overflow: "hidden",
              maxWidth: "100%",
            }}
            onPress={() => setIsShowingTimestamp((prev) => !prev)}
          >
            {/* Render Different Message Types */}
            {message.type === "text" && message.type != "image" ? (
              <View
                style={{
                  backgroundColor: !message.isFromCoach
                    ? Colors.messageLightColor
                    : Colors.messageBubbleColor,
                  padding: 10,
                }}
              >
                <LinkableText
                  style={{
                    color: !message.isFromCoach ? "black" : "white",
                  }}
                  darkBackground={message.isFromCoach}
                >
                  {message.text}
                </LinkableText>
              </View>
            ) : message.type === "image" ? (
              <ImageMessage
                message={message}
                previousSame={previousSame}
                nextSame={nextSame}
              />
            ) : message.type === "audio" ? (
              <AudioRow message={message} />
            ) : (
              message.type === "video" && (
                <SerieMessageView
                  message={message}
                  previousSame={previousSame}
                  nextSame={nextSame}
                />
              )
            )}
          </TouchableOpacity>
          {isShowingTimestamp && (
            <Text
              style={{
                color: Colors.grayColor,
                fontSize: 10,
                marginBottom: 2,
                marginTop: 1,
              }}
            >
              {formatTimestamp(message.timestamp, i18n.language)}
            </Text>
          )}
        </View>
        {!message.isFromCoach && isHovered && (
          <View style={{ alignSelf: "center", flexDirection: "row" }}>
            <TouchableOpacity
              onPress={() =>
                chatManager.setProperty(
                  "selectedReplyToId",
                  message.id.toString()
                )
              }
              style={{
                alignSelf: "center",
                paddingHorizontal: 4,
                marginHorizontal: 4,
              }}
            >
              <Ionicons
                name="arrow-undo-circle"
                size={24}
                color={Colors.messageLightColor}
              />
            </TouchableOpacity>
            {isShowingTimestamp && message.type != "audio" && (
              <TouchableOpacity
                onPress={() => Clipboard.setString(message.text)}
                style={{
                  alignSelf: "center",
                  marginHorizontal: 0,
                }}
              >
                <Ionicons
                  name="copy-outline"
                  size={20}
                  color={Colors.messageLightColor}
                />
              </TouchableOpacity>
            )}
          </View>
        )}
        <Observer>
          {() => (
            <>
              {message.status === MessageStatus.SENDING ? (
                <View
                  style={{
                    width: 4,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: Colors.blackColor,
                    alignSelf: "center",
                    marginHorizontal: 2,
                  }}
                />
              ) : (
                (message.status === MessageStatus.ERROR_MEDIA ||
                  message.status === MessageStatus.ERROR_MESSAGE) && (
                  <View
                    style={{
                      alignItems: "center",
                      justifyContent: "center",
                      alignSelf: "center",
                    }}
                  >
                    <TouchableOpacity
                      onPress={() => {
                        message.client.uploadMessageAndAttachment(message);
                      }}
                      style={{ padding: 8 }}
                    >
                      <Ionicons
                        name={"alert-circle-outline"}
                        size={20}
                        color={"red"}
                      />
                    </TouchableOpacity>
                  </View>
                )
              )}
            </>
          )}
        </Observer>
      </Animated.View>
    );
  }
);

const AudioRow = observer(({ message }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [viewWidth, setViewWidth] = useState(Chat.MEDIA_WIDTH / 1.2);
  const [downloadError, setDownloadError] = useState(false);

  const audioManager = AudioManager.getInstance();
  const isCurrentAudio = audioManager.isSameAudioMessage(message);

  const AudioSlider = observer(() => {
    const isCurrentAudio = audioManager?.isSameAudioMessage(message);
    const sliderValue = isCurrentAudio ? audioManager.audioPositionValue : 0;

    const handleSlidingComplete = async (value) => {
      // 1) Stop any position updates while we seek
      audioManager.stopPositionUpdater();

      // 2) Store the new slider value
      audioManager.setProperty("audioPositionValue", value);

      // 3) If it’s the same message and we have an <audio> element
      if (
        audioManager.isSameAudioMessage(message) &&
        audioManager.audioPlayer
      ) {
        try {
          // a) Seek to the new time (value is in seconds)
          audioManager.audioPlayer.currentTime = value;

          // b) If it was playing before, resume and restart updates
          if (audioManager.isAudioPlaying) {
            await audioManager.audioPlayer.play();
            audioManager.startPositionUpdater();
          }
        } catch (error) {
          console.error("Error seeking playback:", error);
        }

        // 4) Otherwise (slid on a different audio), stop the old one
      } else {
        // a) Pause any existing player
        if (audioManager.audioPlayer) {
          audioManager.audioPlayer.pause();
        }
        // b) Reset state for the new message (sets audioMessagePlayingId, zeroes position)
        // audioManager.resetAudioState(message);
        audioManager.setProperty("audioMessagePlayingId", message.id);
        audioManager.setProperty("isAudioPlaying", false);
      }
    };

    return (
      <TouchableOpacity //To avoid activating the outer touchable opacity when moving the slider
        style={{
          width: viewWidth,
          maxWidth: "100%",
        }}
      >
        <Slider
          value={sliderValue}
          onSlidingStart={() => {
            audioManager?.stopPositionUpdater();
          }}
          onSlidingComplete={handleSlidingComplete}
          minimumValue={0}
          maximumValue={message.totalTime > 0 ? message.totalTime : 0.1}
          step={0.1}
          minimumTrackTintColor={
            message.isFromCoach ? Colors.whiteColor : Colors.blackColor
          }
          maximumTrackTintColor="rgba(255, 255, 255, 0.4)"
          thumbSize={1} // Optional: ensures minimal thumb hitbox
          trackHeight={4} // Optional: adjust as needed
          style={{
            width: "100%",
            height: 26,
            borderRadius: 14,
            flex: 1,
          }}
          sliderLength={
            isCurrentAudio && audioManager.isAudioPlaying
              ? viewWidth - 36
              : viewWidth
          }
          slideOnTap
        />
      </TouchableOpacity>
    );
  });

  useEffect(() => {
    let isMounted = true;
    if (isMounted) setDownloadError(false);

    return () => {
      isMounted = false;
    };
  }, [message?.id]);

  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        paddingRight: 10,
        backgroundColor: !message.isFromCoach
          ? Colors.messageLightColor
          : Colors.messageBubbleColor,
        width: "100%",
      }}
    >
      {/* Play/Pause Button */}
      <TouchableOpacity
        onPress={async () => {
          if (
            audioManager.isAudioPlaying &&
            audioManager.isSameAudioMessage(message)
          ) {
            audioManager.pauseRecording();
            console.log("pauseRecording");
          } else {
            setIsLoading(true);
            try {
              await audioManager.playRecording(message);
              setDownloadError(false);
            } catch (error) {
              console.error("Error playing recording:", error);
              setDownloadError(true);
            }
            setIsLoading(false);
          }
        }}
        style={{
          zIndex: 20,
          paddingLeft: 18,
          paddingRight: 12,
          paddingVertical: 12,
        }}
      >
        {isLoading ? (
          <LoadingLogo
            container={false}
            width={24}
            height={24}
            marginRight={-9}
            dark={!message.isFromCoach}
          />
        ) : (
          <Ionicons
            name={
              downloadError
                ? "alert-circle-outline"
                : audioManager.isAudioPlaying &&
                  audioManager.audioMessagePlayingId?.toString() ===
                    message.id.toString()
                ? "pause"
                : "play"
            }
            size={16}
            color={
              downloadError
                ? "red"
                : message.isFromCoach
                ? "white"
                : Colors.blackColor
            }
            style={{ marginVertical: 4 }}
          />
        )}
      </TouchableOpacity>
      <Observer>
        {() => (
          <>
            {audioManager.isAudioPlaying &&
              audioManager.isSameAudioMessage(message) && (
                <TouchableOpacity
                  onPress={async () => {
                    await audioManager.cyclePlaybackRate();
                  }}
                  style={{
                    padding: 5,
                    paddingVertical: 3,
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    borderRadius: 10,
                    marginRight: 4,
                  }}
                >
                  <Text
                    style={{
                      color: !message.isFromCoach ? "black" : "white",
                      fontSize: 11,
                    }}
                  >
                    {audioManager.playbackRate.toFixed(1)}x
                  </Text>
                </TouchableOpacity>
              )}
            <View
              style={{
                alignItems: "center",
                width: "100%",
                flexShrink: 1,
                marginRight: 8,
                marginLeft: 4,
                zIndex: 2,
              }}
              // onLayout={(event) => {
              //   const { width } = event.nativeEvent.layout;
              //   setViewWidth(width);
              // }}
            >
              <AudioSlider />
            </View>
            {/* Duration Text */}
            <Text
              style={{
                color: message.isFromCoach ? "white" : Colors.blackColor,
                marginLeft: 10,
                fontSize: 11,
                marginRight: 8,
                width: "4ch",
                textAlign: "right",
              }}
            >
              {audioFormatTime(audioManager.getAudioTime(message))}
            </Text>
          </>
        )}
      </Observer>
    </View>
  );
});

const ImageMessage = observer(({ message, nextSame, previousSame }) => {
  const mediaManager = MediaManager.getInstance();
  const [url, setUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadError, setDownloadError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    setUrl(null);
    setIsLoading(true);
    setDownloadError(false);
    const runEffect = async () => {
      if (isMounted) setIsLoading(true);
      try {
        const url = await message.getAttachmentURL();
        if (url) setUrl(url);
      } catch (error) {
        setDownloadError(true);
        console.error("Error checking if video is downloaded:", error);
      }
      if (isMounted) setIsLoading(false);
    };

    runEffect();
    return () => {
      isMounted = false;
    };
  }, [message?.id?.toString(), message.status]);

  return (
    <View
      style={{
        backgroundColor: !message.isFromCoach
          ? Colors.messageLightColor
          : Colors.messageBubbleColor,
        paddingTop: message.text ? 4 : 0,
        paddingHorizontal: message.text ? 4 : 0,
      }}
    >
      {/* Image Container */}
      <TouchableOpacity
        onPress={() => {
          mediaManager.showMediaPlayer(message, undefined);
        }}
        style={{
          width: "100%",
          maxHeight: Chat.MEDIA_HEIGHT,
          overflow: "hidden",
        }}
      >
        {isLoading || message.status === MessageStatus.SENDING ? (
          <View
            style={{
              width: message.text ? Chat.MEDIA_WIDTH - 8 : Chat.MEDIA_WIDTH,
              height: Chat.MEDIA_HEIGHT,
            }}
          >
            <LoadingLogo
              container={false}
              width={50}
              height={50}
              flex={true}
              dark={!message.isFromCoach}
            />
          </View>
        ) : url ? (
          <ImageComponent
            imageURL={url}
            width={"100%"}
            height="100%"
            borderTopLeftRadius={
              previousSame && !message.isFromCoach ? 3 : undefined
            }
            borderTopRightRadius={
              previousSame && message.isFromCoach ? 3 : undefined
            }
            borderBottomLeftRadius={
              nextSame && !message.isFromCoach ? 3 : undefined
            }
            borderBottomRightRadius={
              nextSame && message.isFromCoach ? 3 : undefined
            }
            dark={!message.isFromCoach}
          />
        ) : (
          <View
            style={{
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: Colors.messageLightColor,
              minWidth: Chat.MEDIA_WIDTH,
              height: Chat.MEDIA_HEIGHT,
              borderRadius: 12,
              overflow: "hidden",
              borderTopLeftRadius: previousSame && !message.isFromCoach ? 3 : 8,
              borderTopRightRadius: previousSame && message.isFromCoach ? 3 : 8,
              borderBottomLeftRadius: nextSame && !message.isFromCoach ? 3 : 8,
              borderBottomRightRadius: nextSame && message.isFromCoach ? 3 : 8,
            }}
          >
            <DownloadMediaView error={downloadError} />
          </View>
        )}
      </TouchableOpacity>

      {/* Text */}
      {!!message.text && (
        <LinkableText
          style={{
            color: !message.isFromCoach ? "black" : "white",
            textAlign: "left",
            paddingVertical: 8,
            paddingHorizontal: 4,
            width: "100%",
            alignSelf: "flex-start",
          }}
          darkBackground={message.isFromCoach}
        >
          {message.text}
        </LinkableText>
      )}
    </View>
  );
});

const SerieMessageView = observer(({ message, nextSame, previousSame }) => {
  const { t, i18n } = useTranslation();

  const mediaManager = MediaManager.getInstance();
  const [isLoading, setIsLoading] = useState(true);
  const [url, setUrl] = useState(null);
  const [downloadError, setDownloadError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setUrl(null);
    setIsLoading(true);
    setDownloadError(false);

    const runEffect = async () => {
      if (isMounted) setIsLoading(true);

      try {
        const url = await message.getAttachmentURL(true);
        if (url) setUrl(url);
      } catch (error) {
        setDownloadError(true);
        console.error("Error checking if video is downloaded:", error);
      }
      if (isMounted) setIsLoading(false);
    };

    runEffect();

    return () => {
      isMounted = false;
    };
  }, [message.id.toString()]);

  return (
    <View
      style={{
        backgroundColor: !message.isFromCoach
          ? Colors.messageLightColor
          : Colors.messageBubbleColor,
        paddingTop: message.text ? 4 : 0,
        paddingHorizontal: message.text ? 4 : 0,
        flexShrink: 1,
      }}
      key={message.id.toString()}
    >
      {/* Video Container */}
      <TouchableOpacity
        onPress={() => {
          mediaManager.showMediaPlayer(message, undefined);
        }}
        style={{
          width: "100%",
          maxHeight: Chat.MEDIA_HEIGHT,
          overflow: "hidden",
          flexShrink: 1,
        }}
      >
        {isLoading ? (
          <View
            style={{
              width: message.text ? Chat.MEDIA_WIDTH - 8 : Chat.MEDIA_WIDTH,
              height: Chat.MEDIA_HEIGHT,
            }}
          >
            <LoadingLogo
              container={false}
              width={50}
              height={50}
              flex={true}
              dark={!message.isFromCoach}
            />
          </View>
        ) : url ? (
          <View
            style={{
              width: "100%",
              maxHeight: "100%",
              justifyContent: "center",
              flexShrink: 1,
            }}
          >
            <ImageComponent
              imageURL={url}
              width={"100%"}
              height="100%"
              borderTopLeftRadius={
                previousSame && !message.isFromCoach ? 3 : undefined
              }
              borderTopRightRadius={
                previousSame && message.isFromCoach ? 3 : undefined
              }
              borderBottomLeftRadius={
                nextSame && !message.isFromCoach ? 3 : undefined
              }
              borderBottomRightRadius={
                nextSame && message.isFromCoach ? 3 : undefined
              }
              // keyId={message.refreshed ? "1" : "2"}
              dark={!message.isFromCoach}
            />
            <Ionicons
              name="play-circle"
              size={48}
              color={Colors.whiteColor}
              style={{ position: "absolute", alignSelf: "center" }}
            />

            {message.getSerie &&
              message.getSerie?.exercise?.workout?.week > 0 &&
              message.getSerie?.exercise?.workout?.block?.totalWeeks > 0 && (
                <View
                  style={{
                    padding: 4,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    alignSelf: "flex-end",
                    borderRadius: 8,
                    position: "absolute",
                    right: 4,
                    top: 4,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 11,
                      color: Colors.whiteColor,
                    }}
                    numberOfLines={2}
                  >
                    {t("clientWeekStatus-string", {
                      blockCurrentWeek: message.getSerie.exercise.workout.week,
                      blockWeeks:
                        message.getSerie.exercise.workout.block.totalWeeks,
                    })}
                  </Text>
                </View>
              )}
            <View
              style={{
                width: "100%",
                height: "100%",
                padding: 4,
                alignSelf: "flex-end",
                position: "absolute",
                justifyContent: "flex-end",
                gap: 4,
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  marginLeft: 4,
                  gap: 4,
                }}
              >
                <Ionicons name="videocam" size={14} color={Colors.whiteColor} />
                <Text
                  style={{
                    color: Colors.whiteColor,
                    fontSize: 11,
                  }}
                >
                  {audioFormatTime(message.totalTime)}
                </Text>
              </View>
              {message.getSerie && (
                <>
                  <View
                    style={{
                      padding: 4,
                      backgroundColor: Colors.messageLightColor,
                      alignSelf: "flex-start",
                      borderRadius: 8,
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    {(() => {
                      const parts = message.getSerie.getFormattedTextParts(
                        message.client?.coach?.rirOrRpe,
                        message.client?.coach?.exerciseUnit
                      );
                      return (
                        <>
                          <Text
                            style={{
                              fontSize: 11,
                              color: Colors.blackColor,
                            }}
                          >
                            {parts.weightText}
                          </Text>
                          <Text
                            style={{
                              fontSize: 11,
                              color: Colors.blackColor,
                            }}
                          >
                            {parts.repsText}
                          </Text>
                          <Text
                            style={{
                              fontSize: 11,
                              color: parts.rpeColor,
                            }}
                          >
                            {parts.rpeLabel}
                          </Text>
                          <Text
                            style={{
                              fontSize: 11,
                              color: parts.rpeColor,
                            }}
                          >
                            {parts.rpeValue}
                          </Text>
                        </>
                      );
                    })()}
                  </View>
                  <Text
                    style={{
                      padding: 4,
                      backgroundColor: Colors.messageLightColor,
                      alignSelf: "flex-start",
                      borderRadius: 8,
                      fontSize: 11,
                      color: Colors.blackColor,
                    }}
                    numberOfLines={2}
                  >
                    {message.getSerie.exercise.getName(t, i18n.language)}
                  </Text>
                  {message.getSerie?.exercise?.protocolSlot && (
                    <Text
                      style={{
                        padding: 4,
                        backgroundColor: getSlotColorWithOpacity(
                          message.getSerie.exercise.protocolSlot
                        ),
                        alignSelf: "flex-start",
                        borderRadius: 8,
                        fontSize: 11,
                        color: Colors.whiteColor,
                      }}
                      numberOfLines={1}
                    >
                      {t(message.getSerie.exercise.protocolSlot)}
                    </Text>
                  )}
                </>
              )}
            </View>
          </View>
        ) : (
          <View
            style={{
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: Colors.messageLightColor,
              width: message.text ? Chat.MEDIA_WIDTH - 8 : Chat.MEDIA_WIDTH,
              height: Chat.MEDIA_HEIGHT,
              borderTopLeftRadius: previousSame && !message.isFromCoach ? 3 : 8,
              borderTopRightRadius: previousSame && message.isFromCoach ? 3 : 8,
              borderBottomLeftRadius: nextSame && !message.isFromCoach ? 3 : 8,
              borderBottomRightRadius: nextSame && message.isFromCoach ? 3 : 8,
            }}
          >
            <DownloadMediaView error={downloadError} />
          </View>
        )}
      </TouchableOpacity>
      {/* Text */}
      {!!message.text && (
        <LinkableText
          style={{
            color: !message.isFromCoach ? "black" : "white",
            textAlign: "left",
            paddingVertical: 8,
            paddingHorizontal: 4,
            width: "100%",
            alignSelf: "flex-start",
          }}
          darkBackground={message.isFromCoach}
        >
          {message.text}
        </LinkableText>
      )}
    </View>
  );
});

const ReplyToView = React.memo(({ message }) => {
  const { t } = useTranslation();

  const chatManager = ChatManager.getInstance();

  const replyToMessage = message.client.messages.find(
    (msg) => msg.id === message.replyTo
  );

  if (!replyToMessage) return null;

  const ReplyToImage = React.memo(({ replyToMessage }) => {
    const [url, setUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
      let isMounted = true;
      setUrl(null);
      setIsLoading(true);
      const runEffect = async () => {
        if (isMounted) setIsLoading(true);
        try {
          const url = await replyToMessage.getAttachmentURL();
          if (url) setUrl(url);
        } catch (error) {
          console.error("Error checking if video is downloaded:", error);
        }
        if (isMounted) setIsLoading(false);
      };

      runEffect();
      return () => {
        isMounted = false;
      };
    }, [replyToMessage]);

    return (
      <View
        style={{
          maxWidth: Chat.MEDIA_REPLY_WIDTH,
          maxHeight: Chat.MEDIA_REPLY_HEIGHT,
          overflow: "hidden",
          borderRadius: 10,
        }}
      >
        {url ? (
          <ImageComponent
            imageURL={url}
            width={"100%"}
            height="100%"
            borderRadius={10}
            small={true}
            dark={!replyToMessage.isFromCoach}
          />
        ) : isLoading ? (
          <View
            style={{
              width: Chat.MEDIA_REPLY_WIDTH,
              height: "100%",
              flexGrow: 1,
            }}
          >
            <LoadingLogo
              container={false}
              width={30}
              height={30}
              flex={true}
              dark={!replyToMessage.isFromCoach}
            />
          </View>
        ) : (
          <View
            style={{
              width: Chat.MEDIA_REPLY_WIDTH,
              height: Chat.MEDIA_REPLY_HEIGHT,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Ionicons name="arrow-down-circle" size={18} color="white" />
          </View>
        )}
        <View
          style={{
            position: "absolute",
            bottom: 4,
            left: 4,
            right: 4,
            top: 4,
            justifyContent: "flex-end",
          }}
        >
          {!!replyToMessage.text && (
            <Text
              style={{
                fontSize: 11,
                color: "white",
                backgroundColor: "rgba(108, 108, 108, 0.92)",
                padding: 4,
                borderRadius: 8,
                alignSelf: "flex-start",
              }}
              numberOfLines={2}
            >
              {replyToMessage.text}
            </Text>
          )}
        </View>
      </View>
    );
  });

  const ReplyToVideo = React.memo(({ replyToMessage }) => {
    const [url, setUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
      let isMounted = true;
      setUrl(null);
      setIsLoading(true);
      const runEffect = async () => {
        if (isMounted) {
          setIsLoading(true);
        }

        try {
          const url = await replyToMessage.getAttachmentURL(true);
          if (url) setUrl(url);
        } catch (error) {
          console.error("Error checking if video is downloaded:", error);
        }
        setIsLoading(false);
      };

      runEffect();

      return () => {
        isMounted = false;
      };
    }, [replyToMessage]);

    return (
      <View
        style={{
          width: Chat.MEDIA_REPLY_WIDTH,
          maxHeight: Chat.MEDIA_REPLY_HEIGHT,
          overflow: "hidden",
          borderRadius: 10,
          justifyContent: "center",
        }}
      >
        {isLoading ? (
          <View
            style={{
              height: Chat.MEDIA_REPLY_HEIGHT,
              justifyContent: "center",
            }}
          >
            <LoadingLogo
              container={false}
              width={30}
              height={30}
              dark={!replyToMessage.isFromCoach}
            />
          </View>
        ) : url ? (
          <View
            style={{
              width: "100%",
              maxHeight: "100%",
              justifyContent: "center",
              flexShrink: 1,
            }}
          >
            <ImageComponent
              imageURL={url}
              width={"100%"}
              height="100%"
              borderRadius={10}
              small={true}
              dark={!replyToMessage.isFromCoach}
            />
            <Ionicons
              name="play-circle"
              size={28}
              color={"white"}
              style={{ position: "absolute", alignSelf: "center" }}
            />
          </View>
        ) : (
          <View
            style={{
              flexDirection: "row",
              padding: 8,
              width: Chat.MEDIA_REPLY_WIDTH,
              height: Chat.MEDIA_REPLY_HEIGHT,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Ionicons
              name={url ? "play-circle" : "arrow-down-circle"}
              size={18}
              color="white"
            />
          </View>
        )}
        <View
          style={{
            position: "absolute",
            bottom: 4,
            left: 4,
            right: 4,
            top: 4,
            justifyContent: "flex-end",
          }}
        >
          {!!replyToMessage.text && (
            <Text
              style={{
                fontSize: 11,
                color: "white",
                backgroundColor: "rgba(108, 108, 108, 0.92)",
                padding: 4,
                borderRadius: 8,
                alignSelf: "flex-start",
              }}
              numberOfLines={2}
            >
              {replyToMessage.text}
            </Text>
          )}
        </View>
      </View>
    );
  });

  return (
    <View
      style={{
        paddingVertical: 4,
        alignItems: !message.isFromCoach ? "flex-start" : "flex-end",
        maxWidth: "100%",
      }}
    >
      <Text
        style={{
          fontSize: 12,
          fontWeight: "400",
          color: Colors.messageLightColor,
          marginBottom: 4,
          marginRight: 12,
        }}
        numberOfLines={1}
      >
        {!message.isFromCoach
          ? t("coachReplied-string")
          : t("youReplied-string")}
      </Text>
      <View
        style={{
          flexDirection: !message.isFromCoach ? "row-reverse" : "row",
          justifyContent: !message.isFromCoach ? "flex-end" : "flex-end",
          maxWidth: "100%",
        }}
      >
        <TouchableOpacity
          onPress={() => {
            chatManager.scrollToKey(replyToMessage.id.toString(), true, true);
          }}
          style={{
            padding:
              replyToMessage.type === "image" || replyToMessage.type === "video"
                ? 0
                : replyToMessage.type === "text" ||
                  replyToMessage.type === "audio"
                ? 8
                : 4,
            borderRadius: 10,
            opacity: 0.4,
            backgroundColor: !replyToMessage.isFromCoach
              ? Colors.messageLightColor
              : Colors.messageBubbleColor,
            marginRight: !message.isFromCoach ? 0 : 8,
            marginLeft: !message.isFromCoach ? 8 : 0,
            maxWidth: "100%",
          }}
        >
          {/* Reply Type - Text */}
          {replyToMessage.type === "text" && (
            <Text
              style={{
                fontSize: 14,
                color: !replyToMessage.isFromCoach ? "black" : "white",
                maxWidth: "100%",
              }}
            >
              {replyToMessage.text}
            </Text>
          )}

          {/* Reply Type - Audio */}
          {replyToMessage.type === "audio" && (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                width: Chat.AUDIO_REPLY_WIDTH + 8,
                height: 20,
                justifyContent: "space-between",
                marginHorizontal: 8,
              }}
            >
              <Ionicons
                name={"play"}
                size={16}
                color={!replyToMessage.isFromCoach ? "black" : "white"}
              />
              <View
                style={{
                  flex: 1,
                  height: 4,
                  backgroundColor: "gray",
                  borderRadius: 2,
                  marginHorizontal: 12,
                  opacity: 0.8,
                  flexGrow: 1,
                }}
              />
              <Text
                style={{
                  fontSize: 10,
                  color: !replyToMessage.isFromCoach ? "black" : "white",
                }}
              >
                {audioFormatTime(replyToMessage.totalTime)}
              </Text>
            </View>
          )}

          {/* Reply Type - Image */}
          {replyToMessage.type === "image" && (
            <ReplyToImage replyToMessage={replyToMessage} />
          )}

          {/* Reply Type - Video */}
          {replyToMessage.type === "video" && (
            <ReplyToVideo replyToMessage={replyToMessage} />
          )}
        </TouchableOpacity>

        <View
          style={{
            width: 4,
            backgroundColor: Colors.messageLightColor,
            borderRadius: 2,
            opacity: 0.5,
            alignSelf: "stretch", // this is key in a row layout
          }}
        />
      </View>
    </View>
  );
});

const DownloadMediaView = ({ error }) => {
  return (
    <Ionicons
      name={error ? "alert-circle-outline" : "arrow-down-circle"}
      size={48}
      color={error ? "red" : "white"}
    />
  );
};

export default ChatMessageRowView;
